/**
 * Tonnie CoPilot Guide Engine
 *
 * Core engine that processes CoPilot Guide JSON configurations
 * and manages flow state, transitions, and effects.
 */

import type { CoPilotGuideConfig, Task, Chat, FlowState, TransitionResult } from '../types/guide';

export class CoPilotGuideEngine {
  private config: CoPilotGuideConfig;
  private state: FlowState;
  private chatMap: Map<string, Chat> = new Map();
  private taskMap: Map<string, Task> = new Map();
  private static debugEnabled =
    typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'development';

  private dbg = (...args: unknown[]) => {
    if (CoPilotGuideEngine.debugEnabled) {
      console.log('[CoPilotGuideEngine]', ...args);
    }
  };

  constructor(config: CoPilotGuideConfig) {
    this.dbg(
      'constructor — config id:',
      config?.id,
      'has content:',
      !!config?.content,
      'tasks count:',
      config?.content?.tasks?.length ?? 'N/A'
    );
    this.config = config;
    this.state = this.initializeState();
    this.buildMaps();
    this.dbg('constructor — chatMap size:', this.chatMap.size, 'taskMap size:', this.taskMap.size);
  }

  private initializeState(): FlowState {
    return {
      currentTaskId: null,
      currentChatId: null,
      completedTasks: new Set(),
      completedChats: new Set(),
      taskStates: new Map(),
      chatHistory: [],
      variables: {},
      widgetStates: {},
    };
  }

  private buildMaps(): void {
    // Build chat map for quick lookup
    this.config.content.tasks.forEach((task) => {
      this.taskMap.set(task.id, task);
      task.chats.forEach((chat) => {
        this.chatMap.set(chat.id, chat);
      });
    });
  }

  /**
   * Reset the engine to its initial state (clears all progress, variables, and history)
   */
  public reset(): void {
    this.state = this.initializeState();
    this.dbg('reset() — engine state cleared');
  }

  /**
   * Start the guide flow
   */
  public start(): Chat | null {
    const firstTask = this.config.content.tasks[0];
    this.dbg(
      'start() — firstTask:',
      firstTask?.id ?? 'NONE',
      'total tasks:',
      this.config.content.tasks.length
    );
    if (!firstTask) return null;

    const firstChat = firstTask.chats[0];
    this.dbg(
      'start() — firstChat:',
      firstChat?.id ?? 'NONE',
      'type:',
      firstChat?.type,
      'messages:',
      firstChat?.messages?.length ?? 0,
      'actions:',
      firstChat?.actions?.length ?? 0
    );
    if (!firstChat) return null;

    // Evaluate skip_if on the first chat BEFORE making it current: a skipped
    // opening (e.g. the Journey Builder's welcome for a citizen whose route is
    // already known) was never shown, so it must not become a history entry
    // that "Back" returns to. `transition()` records history only from a
    // current chat.
    if (firstChat.metadata?.skip_if && firstChat.metadata.onComplete) {
      if (this.evaluateCondition(firstChat.metadata.skip_if)) {
        this.dbg(
          'start() — skip_if TRUE for first chat, advancing to:',
          firstChat.metadata.onComplete
        );
        this.state.currentTaskId = null;
        this.state.currentChatId = null;
        this.state.completedChats.add(firstChat.id);
        const result = this.transition(firstChat.metadata.onComplete, firstChat.metadata.effects);
        return result.nextChatId ? (this.chatMap.get(result.nextChatId) ?? null) : null;
      }
    }

    this.state.currentTaskId = firstTask.id;
    this.state.currentChatId = firstChat.id;
    return firstChat;
  }

  /**
   * Get current chat
   */
  public getCurrentChat(): Chat | null {
    if (!this.state.currentChatId) return null;
    return this.chatMap.get(this.state.currentChatId) || null;
  }

  /**
   * Get current task
   */
  public getCurrentTask(): Task | null {
    if (!this.state.currentTaskId) return null;
    return this.taskMap.get(this.state.currentTaskId) || null;
  }

  /**
   * Get chat by ID
   */
  public getChat(chatId: string): Chat | null {
    return this.chatMap.get(chatId) || null;
  }

  /**
   * Get task by ID
   */
  public getTask(taskId: string): Task | null {
    return this.taskMap.get(taskId) || null;
  }

  /**
   * Process transition from an action
   */
  public transition(transitionId: string, effects?: string[]): TransitionResult {
    this.dbg(
      'transition() — target:',
      transitionId,
      'effects:',
      effects,
      'from chat:',
      this.state.currentChatId
    );
    const result: TransitionResult = {
      nextChatId: null,
      effects: effects || [],
      shouldClose: false,
      shouldRedirect: undefined,
    };

    // Handle special transitions.
    //
    // `terminal` and `escape` both close the flow but mean different
    // things to the host: terminal = "finished the conversation, run
    // finalize", escape = "user bailed mid-flow, keep the row active so
    // they can resume". Surface that distinction on the result so the
    // host doesn't have to infer it from the redirect target (which
    // can equal 'dashboard' in either case).
    if (transitionId === 'escape' || transitionId === 'terminal') {
      result.shouldClose = true;
      result.transitionKind = transitionId;
      // Check for redirect effects
      if (effects) {
        const redirectEffect = effects.find((e) => e.startsWith('redirect_'));
        if (redirectEffect) {
          result.shouldRedirect = redirectEffect.replace('redirect_', '');
        }
      }
      if (!result.shouldRedirect && transitionId === 'escape') {
        result.shouldRedirect = 'dashboard';
      }
      return result;
    }

    // Find next chat by ID
    const nextChat = this.chatMap.get(transitionId);
    this.dbg(
      'transition() — found next chat?',
      !!nextChat,
      nextChat
        ? `type=${nextChat.type}, msgs=${nextChat.messages?.length ?? 0}, actions=${nextChat.actions?.length ?? 0}`
        : 'NOT IN MAP'
    );
    if (!nextChat) {
      this.dbg(
        'transition() — WARNING: transitionId not found in chatMap. Available chats:',
        Array.from(this.chatMap.keys())
      );
    }
    if (nextChat) {
      // Evaluate skip_if — if the target chat should be skipped, advance to its onComplete
      if (nextChat.metadata?.skip_if && nextChat.metadata.onComplete) {
        const shouldSkip = this.evaluateCondition(nextChat.metadata.skip_if);
        if (shouldSkip) {
          this.dbg(
            'transition() — skip_if TRUE for chat:',
            transitionId,
            'condition:',
            nextChat.metadata.skip_if,
            '→ advancing to:',
            nextChat.metadata.onComplete
          );
          this.state.completedChats.add(transitionId);
          return this.transition(nextChat.metadata.onComplete, effects);
        }
      }

      // Push current position to chatHistory before transitioning.
      // Snapshot derived state too so `goBack()` can roll it back —
      // without these, walking back after a `set_variable:` /
      // `complete_task:` effect leaves the engine in a state that
      // doesn't match what the user is now seeing on screen.
      if (this.state.currentChatId && this.state.currentTaskId) {
        this.state.chatHistory.push({
          chatId: this.state.currentChatId,
          taskId: this.state.currentTaskId,
          timestamp: new Date(),
          type: 'action',
          data: null,
          variablesSnapshot: { ...this.state.variables },
          completedTasksSnapshot: Array.from(this.state.completedTasks),
          completedChatsSnapshot: Array.from(this.state.completedChats),
        });
      }

      result.nextChatId = transitionId;
      this.state.currentChatId = transitionId;

      // Update task if chat belongs to different task
      for (const [taskId, task] of this.taskMap.entries()) {
        if (task.chats.some((c) => c.id === transitionId)) {
          this.state.currentTaskId = taskId;
          break;
        }
      }
    }

    // Process effects
    if (effects) {
      this.processEffects(effects);
    }

    return result;
  }

  /**
   * Process effects (e.g., complete_task, mark_all_videos_watched)
   */
  private processEffects(effects: string[]): void {
    effects.forEach((effect) => {
      if (effect.startsWith('complete_task:')) {
        const taskId = effect.replace('complete_task:', '');
        this.state.completedTasks.add(taskId);
      } else if (effect === 'mark_all_videos_watched') {
        this.state.variables['allVideosWatched'] = true;
      } else if (effect.startsWith('set_variable:')) {
        const [key, value] = effect.replace('set_variable:', '').split('=');
        if (key) {
          this.state.variables[key] = value ?? true;
        }
      }
      // redirect_ effects are handled in transition result
    });
  }

  /**
   * Mark chat as completed
   */
  public completeChat(chatId: string): TransitionResult | null {
    this.dbg('completeChat() — chatId:', chatId);
    this.state.completedChats.add(chatId);

    // Check for auto-transition
    const chat = this.chatMap.get(chatId);
    if (chat?.metadata?.auto_transition) {
      this.dbg('completeChat() — auto_transition to:', chat.metadata.auto_transition);
      return this.transition(chat.metadata.auto_transition, chat.metadata.effects);
    }

    this.dbg('completeChat() — no auto_transition, returning null');
    return null;
  }

  /**
   * Check if back navigation is possible
   */
  public canGoBack(): boolean {
    return this.state.chatHistory.length > 0;
  }

  /**
   * Navigate back to the previous chat in the history
   */
  public goBack(): TransitionResult {
    if (this.state.chatHistory.length === 0) {
      this.dbg('goBack() — chatHistory is empty, nothing to go back to');
      return { nextChatId: null, effects: [] };
    }

    const previous = this.state.chatHistory.pop()!;
    this.dbg('goBack() — returning to chatId:', previous.chatId, 'taskId:', previous.taskId);

    this.state.currentChatId = previous.chatId;
    this.state.currentTaskId = previous.taskId;

    // Roll back derived state captured at forward-transition time.
    // Old serialized state (pre-snapshot) simply skips this and
    // preserves the legacy behaviour.
    if (previous.variablesSnapshot) {
      this.state.variables = { ...previous.variablesSnapshot };
    }
    if (previous.completedTasksSnapshot) {
      this.state.completedTasks = new Set(previous.completedTasksSnapshot);
    }
    if (previous.completedChatsSnapshot) {
      this.state.completedChats = new Set(previous.completedChatsSnapshot);
    }

    return {
      nextChatId: previous.chatId,
      effects: [],
    };
  }

  /**
   * Handle feature completion
   */
  public handleFeatureComplete(chatId: string, data?: unknown): TransitionResult {
    this.dbg('handleFeatureComplete() — chatId:', chatId, 'data:', data);
    const chat = this.chatMap.get(chatId);

    // Store feature completion data
    if (data) {
      this.state.taskStates.set(`feature_${chatId}`, data);
    }

    if (!chat?.metadata?.onComplete) {
      this.dbg(
        'handleFeatureComplete() — WARNING: no onComplete in metadata for chat:',
        chatId,
        'metadata:',
        chat?.metadata
      );
      return { nextChatId: null, effects: [] };
    }

    this.dbg('handleFeatureComplete() — transitioning to onComplete:', chat.metadata.onComplete);
    this.completeChat(chatId);
    return this.transition(chat.metadata.onComplete, chat.metadata.effects);
  }

  /**
   * Handle feature cancellation
   */
  public handleFeatureCancel(chatId: string): TransitionResult {
    this.dbg('handleFeatureCancel() — chatId:', chatId);
    const chat = this.chatMap.get(chatId);
    if (!chat?.metadata?.onCancel) {
      this.dbg('handleFeatureCancel() — WARNING: no onCancel in metadata for chat:', chatId);
      return { nextChatId: null, effects: [] };
    }

    return this.transition(chat.metadata.onCancel);
  }

  /**
   * Jump back to a previously-visited chat for editing without
   * destroying the forward history. Used by the transcript view's
   * "Edit" affordance: the user clicks Edit on a past Q&A summary;
   * the engine sets `currentChatId` back to that chat; the user
   * re-submits; we walk forward again, re-evaluating conditions.
   *
   * Semantics:
   *   • The target chat must already be in `chatHistory` (forward-only
   *     edits would be a destructive jump and aren't supported).
   *   • `currentChatId` / `currentTaskId` are restored from the stack
   *     entry for that chat. Any later `chatHistory` entries (newer
   *     forward steps) are KEPT — they form the "stale forward path"
   *     the engine will re-validate when the user advances again.
   *   • `completedChats` / `completedTasks` / `variables` are rolled
   *     back to the snapshot captured at that history entry, matching
   *     `goBack()` semantics. Downstream chats that depended on the
   *     edited variables get re-evaluated naturally because their
   *     `skip_if` / `condition` are checked on transition.
   *
   * Returns the chat to render (or null if `chatId` isn't in history).
   */
  public editChat(chatId: string): Chat | null {
    const idx = this.state.chatHistory.findIndex((e) => e.chatId === chatId);
    if (idx < 0) {
      this.dbg('editChat() — chat is not in history; refusing forward jump:', chatId);
      return null;
    }
    const entry = this.state.chatHistory[idx];
    this.dbg('editChat() — jumping to', chatId, 'at history index', idx);

    this.state.currentChatId = entry.chatId;
    this.state.currentTaskId = entry.taskId;

    // Roll back derived state to the snapshot captured BEFORE the user
    // moved forward from this chat. Same rollback `goBack()` performs,
    // but we don't pop the history — leaving newer entries lets the
    // transcript still render the forward path the user took.
    if (entry.variablesSnapshot) {
      this.state.variables = { ...entry.variablesSnapshot };
    }
    if (entry.completedTasksSnapshot) {
      this.state.completedTasks = new Set(entry.completedTasksSnapshot);
    }
    if (entry.completedChatsSnapshot) {
      this.state.completedChats = new Set(entry.completedChatsSnapshot);
    }

    // Truncate history to the edit point — when the user advances
    // again, transition() will append fresh entries. Without this, a
    // user editing chat #3 then advancing would have history #3, #4,
    // #5 (old forward) plus #3' (re-entered) and the chronology breaks.
    this.state.chatHistory = this.state.chatHistory.slice(0, idx);

    return this.chatMap.get(chatId) ?? null;
  }

  /**
   * Get flow state
   */
  public getState(): FlowState {
    return {
      ...this.state,
      completedTasks: new Set(this.state.completedTasks),
      completedChats: new Set(this.state.completedChats),
      taskStates: new Map(this.state.taskStates),
    };
  }

  /**
   * Restore flow state. Accepts both `FlowState` (live) and the
   * deserialized `SerializableFlowState` shape (arrays/Records that
   * are converted back to Sets/Maps internally).
   *
   * widgetStates is restored atomically so widgets render with their
   * persisted answers on first paint — no flicker, no race with the
   * lifecycle context query.
   */
  public restoreState(
    state: Partial<FlowState> & { widgetStates?: Record<string, unknown> }
  ): void {
    if (state.currentTaskId !== undefined) {
      this.state.currentTaskId = state.currentTaskId;
    }
    if (state.currentChatId !== undefined) {
      this.state.currentChatId = state.currentChatId;
    }
    if (state.completedTasks) {
      this.state.completedTasks = new Set(state.completedTasks);
    }
    if (state.completedChats) {
      this.state.completedChats = new Set(state.completedChats);
    }
    if (state.taskStates) {
      this.state.taskStates = new Map(state.taskStates);
    }
    if (state.chatHistory) {
      this.state.chatHistory = [...state.chatHistory];
    }
    if (state.variables) {
      this.state.variables = { ...state.variables };
    }
    if (state.widgetStates) {
      this.state.widgetStates = { ...state.widgetStates };
    }
  }

  /**
   * Read widget state for a given key (`<chatId>::<widgetName>`).
   * Returns undefined if no state has been persisted for that widget yet.
   */
  public getWidgetState<T = unknown>(widgetKey: string): T | undefined {
    return this.state.widgetStates[widgetKey] as T | undefined;
  }

  /**
   * Merge-patch widget state. Caller supplies a partial; engine merges
   * into the existing slice (or seeds it if absent). Returns the new
   * state for callers that want to forward it to a server PATCH.
   */
  public patchWidgetState<T extends Record<string, unknown>>(
    widgetKey: string,
    partial: Partial<T>
  ): T {
    const prev = (this.state.widgetStates[widgetKey] ?? {}) as T;
    const next = { ...prev, ...partial } as T;
    this.state.widgetStates[widgetKey] = next;
    return next;
  }

  /** Drop a single widget's persisted state (used on retake/restart per-widget). */
  public clearWidgetState(widgetKey: string): void {
    delete this.state.widgetStates[widgetKey];
  }

  /** Snapshot of all widget states — used by useGuideSession when serializing. */
  public getAllWidgetStates(): Record<string, unknown> {
    return { ...this.state.widgetStates };
  }

  /**
   * Get all tasks
   */
  public getTasks(): Task[] {
    return this.config.content.tasks;
  }

  /**
   * Check if task is completed
   */
  public isTaskCompleted(taskId: string): boolean {
    return this.state.completedTasks.has(taskId);
  }

  /**
   * Check if chat is completed
   */
  public isChatCompleted(chatId: string): boolean {
    return this.state.completedChats.has(chatId);
  }

  /**
   * Set variable
   */
  public setVariable(key: string, value: unknown): void {
    this.state.variables[key] = value;
  }

  /**
   * Get variable
   */
  public getVariable(key: string): unknown {
    return this.state.variables[key];
  }

  /**
   * Get all collected variables as a shallow copy.
   */
  public getAllVariables(): Record<string, unknown> {
    return { ...this.state.variables };
  }

  /**
   * Replace template variables in text (e.g., {nickname}, {englishLevel})
   */
  public replaceVariables(text: string): string {
    return text.replace(/\{(\w+)\}/g, (match, key) => {
      const value = this.state.variables[key];
      return value !== undefined ? String(value) : match;
    });
  }

  /**
   * Evaluate condition expression against current variables.
   * Supported: `var == 'val'`, `var != 'val'`, `var in ['a','b']`
   */
  public evaluateCondition(condition: string): boolean {
    // Equality: "variable == 'value'"
    const eqMatch = condition.match(/^(\w+)\s*==\s*['"]?(.+?)['"]?$/);
    if (eqMatch) {
      const [, variable, value] = eqMatch;
      return String(this.state.variables[variable]) === value;
    }

    // Inequality: "variable != 'value'"
    const neqMatch = condition.match(/^(\w+)\s*!=\s*['"]?(.+?)['"]?$/);
    if (neqMatch) {
      const [, variable, value] = neqMatch;
      return String(this.state.variables[variable]) !== value;
    }

    // Membership: "variable in ['A', 'B']"
    const inMatch = condition.match(/^(\w+)\s+in\s+\[(.+)\]$/);
    if (inMatch) {
      const [, variable, values] = inMatch;
      const valueList = values.split(',').map((v) => v.trim().replace(/['"]/g, ''));
      return valueList.includes(String(this.state.variables[variable]));
    }

    return true;
  }

  /**
   * Get completion percentage (0–100).
   *
   * Uses task-level position rather than individual chat counts.
   *
   * Why task-based:
   *  - Flows branch: some chats are skipped by direct action transitions so
   *    they never appear in completedChats. A chat-count formula can therefore
   *    never reach 100 % on a flow with optional/conditional chats.
   *  - Tasks represent the major stages visible to the user (Intro, Selection,
   *    Processing, Confirmation …). Basing progress on which task the user is
   *    currently in gives a stable, correct reading for all paths.
   *
   * Rules:
   *  1. Terminal / Escape tasks are flow-control exits → return 100.
   *  2. Otherwise, position = (currentTaskIndex + 1) / totalVisibleTasks × 100,
   *     capped at 100.  Being on the last visible task always returns 100.
   */
  public getCompletionPercentage(): number {
    const CONTROL_TYPES = new Set(['Terminal', 'Escape']);

    // Rule 1: flow-control task → already done
    const currentTask = this.getCurrentTask();
    if (currentTask && CONTROL_TYPES.has(currentTask.type)) {
      return 100;
    }

    // Visible (user-facing) tasks only
    const visibleTasks = this.config.content.tasks.filter((task) => !CONTROL_TYPES.has(task.type));
    if (visibleTasks.length === 0) return 0;

    // Rule 2: task position
    const currentIdx = visibleTasks.findIndex((t) => t.id === this.state.currentTaskId);
    if (currentIdx < 0) return 0;

    return Math.min(100, Math.round(((currentIdx + 1) / visibleTasks.length) * 100));
  }

  /**
   * Get config metadata
   */
  public getConfig(): CoPilotGuideConfig {
    return this.config;
  }
}
